import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Bell, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { smsOffActionText } from '../common/SmsOffBadge';
import './NotificationPreferencesCard.css';

/**
 * NotificationPreferencesCard
 *
 * One row per proactive message type, each with a Web and an SMS switch.
 * Used on the member Profile page (own prefs) and inside the admin user
 * modal (any user) — the caller supplies `load` / `save`.
 *
 * Rules (mirrored server-side):
 *   • Web off ⇒ SMS off, in the same click, one save.
 *   • SMS switch is disabled while Web is off.
 *   • Turning Web back on does NOT turn SMS back on.
 *
 * Each switch saves immediately (optimistic), reverts + toasts on failure.
 *
 * Master "Text messages (SMS)" switch (#1108): shown when the API returns
 * `sms` next to `types` ({sms_consent, sms_opt_out, requires_start_text,
 * sms_sender_number}). While it is off every per-type SMS switch is locked.
 * A member who opted out by texting STOP is still blocked by Twilio until
 * they text START — the hint under the switch says so.
 *
 * @param {function} load   async () => { types: [{key,label,description,web,sms}], sms? }
 * @param {function} save   async (partial) => same shape; partial = { [key]: {web?, sms?} }
 * @param {function} onSmsToggle  async (enabled) => SmsConsentState. Optional; without
 *                                it the master switch is informational (disabled).
 * @param {boolean}  readOnly  render switches disabled (admin without users:update)
 * @param {string}   id     DOM id; if the URL hash matches, the card scrolls into view
 * @param {boolean}  showHeader  render the card header (false when embedded in a modal)
 */
const MASTER_KEY = '__sms__';

const NotificationPreferencesCard = ({
  load,
  save,
  onSmsToggle,
  audience = 'member',
  readOnly = false,
  id = 'notifications',
  showHeader = true,
}) => {
  const [types, setTypes] = useState(null);
  const [sms, setSms] = useState(null);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const masterOff = Boolean(sms) && sms.sms_consent === false;
  // Twilio-enforced STOP: only the member's START text lifts it. Nobody can
  // turn it on from here (member or admin) — plan §9.
  const masterLocked = masterOff && Boolean(sms.requires_start_text);
  const offMessage = masterOff ? smsOffActionText(sms, audience) : '';

  useEffect(() => {
    let alive = true;
    setError(null);
    Promise.resolve()
      .then(() => load())
      .then((data) => {
        if (!alive) return;
        setTypes(Array.isArray(data?.types) ? data.types : []);
        setSms(data?.sms && typeof data.sms === 'object' ? data.sms : null);
      })
      .catch(() => {
        if (alive) setError('Could not load notification preferences.');
      });
    return () => {
      alive = false;
    };
  }, [load]);

  // Deep link: /profile#notifications scrolls here once the rows exist.
  useEffect(() => {
    if (!types || typeof window === 'undefined') return;
    if (window.location.hash === `#${id}`) {
      const el = document.getElementById(id);
      if (el && typeof el.scrollIntoView === 'function') {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      }
    }
  }, [types, id]);

  const toggle = useCallback(
    async (key, channel) => {
      if (!types || readOnly || savingKey) return;
      const row = types.find((t) => t.key === key);
      if (!row) return;

      let next;
      if (channel === 'web') {
        const web = !row.web;
        // Web off drags SMS off. Web on leaves SMS as it was.
        next = web ? { web: true } : { web: false, sms: false };
      } else {
        if (masterOff) {
          // Greyed, not toggled: tell them what to do instead (plan §9).
          toast.error(offMessage);
          return;
        }
        if (!row.web) return; // SMS is locked while Web is off
        next = { sms: !row.sms };
      }

      const previous = types;
      setTypes(types.map((t) => (t.key === key ? { ...t, ...next } : t)));
      setSavingKey(key);
      try {
        const data = await save({ [key]: next });
        if (Array.isArray(data?.types)) setTypes(data.types);
        if (data?.sms && typeof data.sms === 'object') setSms(data.sms);
        if (channel === 'web' && next.web === false && row.sms) {
          toast.success(`${row.label}: Web and SMS turned off.`);
        }
      } catch (e) {
        setTypes(previous);
        toast.error('Could not save. Please try again.');
      } finally {
        setSavingKey(null);
      }
    },
    [types, readOnly, savingKey, save, masterOff, offMessage]
  );

  // Master "Text messages" switch — its own saver (member: own prefs endpoint,
  // admin: /admin/users/{id}/sms-consent). Optimistic, reverts on failure.
  const toggleMaster = useCallback(async () => {
    if (!sms || !onSmsToggle || readOnly || savingKey || masterLocked) return;
    const next = !sms.sms_consent;
    const previous = sms;
    setSms({ ...sms, sms_consent: next });
    setSavingKey(MASTER_KEY);
    try {
      const result = await onSmsToggle(next);
      setSms(
        result && typeof result === 'object' && 'sms_consent' in result
          ? result
          : { ...previous, sms_consent: next }
      );
    } catch (e) {
      setSms(previous);
      toast.error('Could not save. Please try again.');
    } finally {
      setSavingKey(null);
    }
  }, [sms, onSmsToggle, readOnly, savingKey, masterLocked]);

  const renderSwitch = (row, channel) => {
    // While the master is off, per-type SMS switches are SHOWN off and greyed
    // (saved values are kept and come back when SMS is on again). They stay
    // clickable so the click can explain what to do — plan §9 rule 3.
    const greyedByMaster = channel === 'sms' && masterOff;
    const on = channel === 'web' ? row.web : row.sms && !masterOff;
    const lockedByWeb = channel === 'sms' && !row.web && !masterOff;
    const disabled = readOnly || lockedByWeb || savingKey === row.key;
    const label = `${row.label} ${channel === 'web' ? 'Web' : 'SMS'}`;
    let title = label;
    if (lockedByWeb) title = 'SMS is off because Web is off. Turn Web on to enable SMS.';
    if (greyedByMaster) title = offMessage;
    return (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-disabled={greyedByMaster ? 'true' : undefined}
        aria-label={label}
        title={title}
        className={`np-switch ${on ? 'on' : ''} ${disabled || greyedByMaster ? 'disabled' : ''}`}
        disabled={disabled}
        onClick={() => toggle(row.key, channel)}
        data-testid={`np-${row.key}-${channel}`}
      >
        <span className="np-knob" />
      </button>
    );
  };

  return (
    <div id={id} className="card card-animate np-card">
      {showHeader && (
        <div className="card-header">
          <div className="card-title-wrapper">
            <Bell size={24} className="card-icon" />
            <h2 className="card-title">Notification Preferences</h2>
          </div>
        </div>
      )}

      <p className="np-note">
        <Info size={14} />
        SMS is a copy of what you see in web chat. Turning Web off also turns SMS off.
      </p>

      {error && <div className="np-error">{error}</div>}
      {!error && !types && <div className="np-loading">Loading…</div>}

      {types && sms && (
        <div className="np-rows np-master" role="list" data-testid="np-master-row">
          <div className="np-row" role="listitem">
            <div className="np-text">
              <div className="np-label">Text messages (SMS)</div>
              <div className="np-desc">
                Master switch for every text from Concierge. Web chat is not affected.
              </div>
              {offMessage && (
                <div className="np-hint" data-testid="np-sms-message">{offMessage}</div>
              )}
            </div>
            <div className="np-col" />
            <div className="np-col">
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(sms.sms_consent)}
                aria-label="Text messages (SMS)"
                title={
                  masterLocked
                    ? offMessage
                    : onSmsToggle ? 'Turn all text messages on or off' : 'Text messages (SMS)'
                }
                className={`np-switch ${sms.sms_consent ? 'on' : ''} ${
                  readOnly || !onSmsToggle || masterLocked || savingKey === MASTER_KEY ? 'disabled' : ''
                }`}
                disabled={readOnly || !onSmsToggle || masterLocked || savingKey === MASTER_KEY}
                onClick={toggleMaster}
                data-testid="np-master-sms"
              >
                <span className="np-knob" />
              </button>
            </div>
          </div>
        </div>
      )}

      {types && (
        <div className="np-rows" role="list">
          <div className="np-row np-row-head" aria-hidden="true">
            <div />
            <div className="np-col">Web</div>
            <div className="np-col">SMS</div>
          </div>
          {types.map((row) => (
            <div key={row.key} className="np-row" role="listitem" data-testid={`np-row-${row.key}`}>
              <div className="np-text">
                <div className="np-label">{row.label}</div>
                <div className="np-desc">{row.description}</div>
                {!row.web && (
                  <div className="np-hint" data-testid={`np-hint-${row.key}`}>
                    SMS is off because Web is off. Turn Web on to enable SMS.
                  </div>
                )}
              </div>
              <div className="np-col">{renderSwitch(row, 'web')}</div>
              <div className="np-col">{renderSwitch(row, 'sms')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

NotificationPreferencesCard.propTypes = {
  load: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  onSmsToggle: PropTypes.func,
  audience: PropTypes.oneOf(['member', 'admin']),
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  showHeader: PropTypes.bool,
};

export default NotificationPreferencesCard;
